import { DashboardPrices, SaleTerm, SubscriptionType } from 'constants/payments';

/**
 * The Dashboard offers the in-app modal makes every day, the same ones the Settings dialog always
 * made (Rob, 2026-10-10): the annual plan at its list price with the 20% once upgrade coupon, and
 * Lifetime at its full list price. Lifetime is discounted only on the post-checkout page, or in a
 * scheduled member sale at or above `MEMBER_LIFETIME_SALE_FLOOR_CENTS`. Technical Analysis keeps
 * the Settings dialog it always had (Rob, 2026-10-14) and gets no member offers.
 */
export const MEMBER_OFFER_PRICES = {
  annual: DashboardPrices.annual,
  lifetime: DashboardPrices.lifetime,
};

/** The lowest Lifetime price a Dashboard member sale can charge. */
export const MEMBER_LIFETIME_SALE_FLOOR_CENTS = 100000;

/** A Stripe object that exists separately in test mode and in live mode. */
export interface MemberSaleStageValue {
  development: string;
  production: string;
}

export const memberSaleStageValue = (value: MemberSaleStageValue) =>
  value[process.env.APP_STAGE === 'production' ? 'production' : 'development'];

export interface MemberSaleCampaign {
  id: string;
  title: string;
  product: SubscriptionType;
  startsAt: string;
  /**
   * The sale's last instant (exclusive). Every sale ends on a date in code, as the landing banner's
   * do. A promotion or coupon that expires earlier ends the sale at that moment. Stripe cannot move
   * a promotion's expiry, so extending a sale means a new date here and a deploy.
   */
  endsAt: string;
  /** The annual discount: a Stripe promotion code, 40% at most, once, forever or 12 months. */
  promotionCode?: MemberSaleStageValue;
  /** A Lifetime sale: a one-time Stripe price on the Lifetime product, at or above the floor. */
  lifetimePrice?: MemberSaleStageValue;
  /** Plans the sale reaches. Annual members can only receive a Lifetime sale. */
  sourceTerms: Array<SaleTerm.Monthly | SaleTerm.Quarterly | SaleTerm.Annual>;
}

/**
 * Like topBannerConfig: preload UTC windows and select them at runtime. Add a campaign only after
 * its test and live Stripe objects exist. There is no resetting evergreen deadline. Pause a running
 * sale by deactivating its promotion in Stripe. See
 * docs/runbooks/member-offers.md.
 */
export const MEMBER_SALES_SCHEDULE: MemberSaleCampaign[] = [
  // The member versions of the 2026 holiday sales (Rob, 2026-11-03): the same dates as the site's
  // banner, forever instead of the first payment, and only for switching a Monthly or Quarterly
  // plan to Annual. Annual and Lifetime members get no annual sale; there is no Lifetime sale.
  {
    id: 'black-friday-2026-member-annual',
    title: 'Black Friday member sale',
    product: SubscriptionType.DashboardStandard,
    sourceTerms: [SaleTerm.Monthly, SaleTerm.Quarterly],
    startsAt: '2026-11-13T08:00:00Z', // Fri Nov 13, 12:00 AM PST
    endsAt: '2026-12-05T08:00:00Z', // through Fri Dec 4, 11:59 PM PST
    promotionCode: { development: 'MEMBER40', production: 'MEMBER40' },
  },
  {
    id: 'new-year-2026-member-annual',
    title: 'New Year member sale',
    product: SubscriptionType.DashboardStandard,
    sourceTerms: [SaleTerm.Monthly, SaleTerm.Quarterly],
    startsAt: '2026-12-05T08:00:00Z', // Sat Dec 5, 12:00 AM PST
    endsAt: '2027-01-05T08:00:00Z', // through Mon Jan 4, 11:59 PM PST
    promotionCode: { development: 'MEMBER30', production: 'MEMBER30' },
  },
];

export type MemberSaleKind = 'annual' | 'lifetime';

const isOpen = (sale: MemberSaleCampaign, now: number) =>
  now >= Date.parse(sale.startsAt) && now < Date.parse(sale.endsAt);

export const getActiveMemberSale = (
  product: SubscriptionType,
  term: SaleTerm,
  now = Date.now(),
  schedule = MEMBER_SALES_SCHEDULE,
  kind: MemberSaleKind = 'annual',
) =>
  schedule.find(
    (sale) =>
      sale.product === product &&
      sale.sourceTerms.some((source) => source === term) &&
      (kind === 'annual' ? !!sale.promotionCode : !!sale.lifetimePrice) &&
      isOpen(sale, now),
  ) || null;

/**
 * A sale's last day, as members read it: the instant it ends is exclusive, so the day before midnight
 * is printed. Sales end at midnight Pacific, as the site banner's do, so the day is read in Pacific
 * time; in the viewer's own zone a 3:00 AM Eastern end would print the next day.
 */
export const getMemberSaleLastDay = (endsAt: number) =>
  new Date(endsAt - 1).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  });

export const getNextMemberSaleBoundary = (now = Date.now(), schedule = MEMBER_SALES_SCHEDULE) => {
  const future = schedule
    .flatMap((sale) => [Date.parse(sale.startsAt), Date.parse(sale.endsAt)])
    .filter((date) => Number.isFinite(date) && date > now);
  return future.length ? Math.min(...future) : null;
};
